import type { ApolloError } from '@common/hooks';

import { captureException } from 'works/legacy/lib/sentry';

import { ERROR_TYPES } from './error-handler-constants';

type NotifiableError = string | Error;

function areAllErrorsWithSameCode(
  errorsCode: (string | undefined)[],
  firstErrorCode: string | undefined
) {
  return errorsCode.every((errorCode) => errorCode === firstErrorCode);
}

function getErrorsCodeList(graphQLErrors: { extensions?: { code: string } }[]) {
  return graphQLErrors.map((error) => error?.extensions?.code);
}

export function getCodeFromErrors(graphQLErrors: unknown) {
  if (!Array.isArray(graphQLErrors)) {
    return ERROR_TYPES.UNEXPECTED;
  }

  const errorsCode = getErrorsCodeList(graphQLErrors);
  const [firstErrorCode] = errorsCode;

  if (areAllErrorsWithSameCode(errorsCode, firstErrorCode)) {
    return firstErrorCode;
  }

  return ERROR_TYPES.UNEXPECTED;
}

// Reads one ID-free extension discriminator (other than `code`, which getCodeFromErrors owns) — e.g.
// constraintKey, reason, currentState — off the first GraphQL error. The backend adds these so the
// caller's copy can be specific; returns undefined when absent (or non-string, e.g. null) so callers
// fall back to generic copy. The raw backend message can carry internal IDs and is never surfaced.
export function getExtensionFromErrors(
  graphQLErrors: ApolloError['graphQLErrors'],
  field: string
): string | undefined {
  const value = graphQLErrors?.[0]?.extensions?.[field];
  return typeof value === 'string' ? value : undefined;
}

export function notifyUnhandledError(error: NotifiableError, log?: string) {
  console.error(`'ErrorHandler: ${log || ''}`, error);
  captureException(error);
}
